import type { ReactElement } from 'react';
import {
  VERZEICHNISROLLE_BESCHRIFTUNG,
  VERZEICHNISZUSTAND_BESCHRIFTUNG,
  ZEIT_PLATZHALTER,
} from '@mcikm-vote/shared';
import type { SystemWarnung, SystemstatusAntwort } from '@mcikm-vote/shared';

import { useSystemstatus } from '../../admin/systemApi';
import { formatiereGroesse } from '../../components/ExportFormatZeile';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { Karte } from '../../components/Karte';
import { formatiereZeitpunkt } from '../../formatierung';
import { ZuruecksetzenAbschnitt } from './ZuruecksetzenAbschnitt';

/**
 * Systemstatus und Speicherplatzverwaltung (§20.1, §20.4, §32).
 *
 * Die Seite zeigt ausschließlich, was der Server liefert — und der liefert weder Geheimnisse noch
 * vollständige Umgebungsvariablen noch absolute Pfade (§20.4). Verzeichnisse erscheinen deshalb
 * über ihre Rolle, nicht über ihren Ort.
 *
 * Warnungen stehen oben und nicht am Ende: Wer diese Seite aufruft, sucht meist genau sie.
 */
export function SystemstatusSeite(): ReactElement {
  const abfrage = useSystemstatus();
  const stand = abfrage.data;

  if (abfrage.error !== null && abfrage.error !== undefined) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">Systemstatus</h1>
        <Fehlermeldung>{abfrage.error.message}</Fehlermeldung>
      </div>
    );
  }

  if (stand === undefined) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">Systemstatus</h1>
        <p className="text-text-gedaempft text-sm">Der Systemstatus wird erhoben …</p>
      </div>
    );
  }

  const zeit = (wert: string | null): string =>
    wert === null ? ZEIT_PLATZHALTER : formatiereZeitpunkt(wert, stand.zeitzone);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-semibold">Systemstatus</h1>

      {stand.warnungen.length > 0 && (
        <ul className="flex flex-col gap-2">
          {stand.warnungen.map((warnung) => (
            <li key={warnung.text}>
              <WarnungsZeile warnung={warnung} />
            </li>
          ))}
        </ul>
      )}

      <Karte>
        <h2 className="text-base font-semibold">Anwendung</h2>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <Angabe beschriftung="Version" wert={stand.version} />
          <Angabe beschriftung="Betriebsmodus" wert={stand.betriebsmodus} />
          <Angabe beschriftung="Anzeigezeitzone" wert={stand.zeitzone} />
          <Angabe beschriftung="Laufzeit seit Start" wert={laufzeit(stand.laufzeitSekunden)} />
          <Angabe
            beschriftung="Zugriff"
            wert={stand.verschluesselt ? 'verschlüsselt (HTTPS)' : 'unverschlüsselt (HTTP)'}
          />
          <Angabe beschriftung="Stand" wert={zeit(stand.zeitpunkt)} />
        </dl>
      </Karte>

      <Karte>
        <h2 className="text-base font-semibold">Datenbank</h2>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <Angabe
            beschriftung="SQLite"
            wert={stand.datenbank.geoeffnet ? 'geöffnet' : 'nicht geöffnet'}
          />
          <Angabe
            beschriftung="Integritätsprüfung"
            wert={stand.datenbank.integritaet === 'ok' ? 'ohne Befund' : 'fehlgeschlagen'}
          />
          <Angabe
            beschriftung="Letzte Prüfung beim Start"
            wert={zeit(stand.datenbank.letztePruefung)}
          />
          <Angabe beschriftung="Größe" wert={formatiereGroesse(stand.datenbank.groesseBytes)} />
          <Angabe
            beschriftung="Angewendete Migrationen"
            wert={String(stand.datenbank.migrationen)}
          />
        </dl>
      </Karte>

      <Karte>
        <h2 className="text-base font-semibold">Verzeichnisse und Speicherplatz</h2>

        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          {stand.verzeichnisse.map((verzeichnis) => (
            <Angabe
              key={verzeichnis.rolle}
              beschriftung={VERZEICHNISROLLE_BESCHRIFTUNG[verzeichnis.rolle]}
              wert={`${VERZEICHNISZUSTAND_BESCHRIFTUNG[verzeichnis.zustand]} · ${formatiereGroesse(
                verzeichnis.groesseBytes,
              )}`}
            />
          ))}
        </dl>

        <div className="mt-4">
          <Speicherbalken speicher={stand.speicher} />
        </div>
      </Karte>

      <Karte>
        <h2 className="text-base font-semibold">Backups und Echtzeitkanal</h2>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <Angabe
            beschriftung="Automatische Sicherungsstände"
            wert={`${stand.backups.sicherungsstaende} von höchstens ${stand.backups.sicherungsstaendeMax}`}
          />
          <Angabe
            beschriftung="Letztes erfolgreiches Backup"
            wert={zeit(stand.backups.letztesErfolgreiches)}
          />
          <Angabe
            beschriftung="Echtzeitkanal"
            wert={stand.echtzeit.verfuegbar ? 'betriebsbereit' : 'nicht betriebsbereit'}
          />
          <Angabe beschriftung="Verbundene Geräte" wert={String(stand.echtzeit.verbindungen)} />
          <Angabe
            beschriftung="Rückfall-Polling"
            wert={stand.echtzeit.pollingAktiv ? 'aktiv' : 'nicht aktiv'}
          />
        </dl>
      </Karte>

      {/*
        Ganz am Ende und nur, wenn eingerichtet: Der Gefahrenbereich soll niemandem begegnen, der
        den Systemstatus zum Nachsehen aufruft (ÄA-3).
      */}
      {stand.zuruecksetzenVerfuegbar && <ZuruecksetzenAbschnitt />}
    </div>
  );
}

function Angabe({ beschriftung, wert }: { beschriftung: string; wert: string }): ReactElement {
  return (
    <div className="flex flex-col">
      <dt className="text-text-gedaempft text-xs">{beschriftung}</dt>
      <dd className="text-sm">{wert}</dd>
    </div>
  );
}

/**
 * Warnung oder Hinweis.
 *
 * Nicht allein über Farbe unterschieden (§21): Das Wort steht davor.
 */
function WarnungsZeile({ warnung }: { warnung: SystemWarnung }): ReactElement {
  const klassen =
    warnung.stufe === 'warnung'
      ? 'border-fehler/50 bg-fehler/10'
      : 'border-warnung/50 bg-warnung/10';

  return (
    <p className={`rounded-md border p-3 text-sm ${klassen}`}>
      <strong>{warnung.stufe === 'warnung' ? 'Warnung: ' : 'Hinweis: '}</strong>
      {warnung.text}
    </p>
  );
}

/** Belegung des Datenträgers als Balken mit Zahlen daneben (§21: nicht nur farbig). */
function Speicherbalken({ speicher }: { speicher: SystemstatusAntwort['speicher'] }): ReactElement {
  const anteil = speicher.gesamtBytes === 0 ? 0 : speicher.belegtBytes / speicher.gesamtBytes;
  const prozent = Math.round(anteil * 100);

  return (
    <div>
      <div className="flex justify-between text-sm">
        <span>Datenträger zu {prozent} Prozent belegt</span>
        <span className="text-text-gedaempft">
          {formatiereGroesse(speicher.freiBytes)} von {formatiereGroesse(speicher.gesamtBytes)} frei
        </span>
      </div>
      <div
        className="bg-flaeche-gedaempft mt-1 h-3 w-full overflow-hidden rounded-full"
        role="img"
        aria-label={`${prozent} Prozent des Datenträgers belegt`}
      >
        <div
          className={anteil > 0.9 ? 'bg-fehler h-full' : 'bg-akzent h-full'}
          style={{ width: `${Math.min(prozent, 100)}%` }}
        />
      </div>
    </div>
  );
}

/** Laufzeit in Tagen, Stunden und Minuten. */
function laufzeit(sekunden: number): string {
  const tage = Math.floor(sekunden / 86_400);
  const stunden = Math.floor((sekunden % 86_400) / 3_600);
  const minuten = Math.floor((sekunden % 3_600) / 60);

  if (tage > 0) {
    return `${tage} Tage, ${stunden} Std. ${minuten} Min.`;
  }
  if (stunden > 0) {
    return `${stunden} Std. ${minuten} Min.`;
  }
  return `${minuten} Min.`;
}
